import React, { useState } from 'react'
import Chart from 'chart.js'
import { HorizontalBar } from 'react-chartjs-2'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardHeader from '@material-ui/core/CardHeader'
import Collapse from '@material-ui/core/Collapse'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import BootstrapTable from 'react-bootstrap-table-next'
import filterFactory from 'react-bootstrap-table2-filter'
import { chartOptions, parseOptions } from 'config/constants/ChartVariables'
import { pensionTypes } from 'config/constants/Constants'

import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import chartComponentStyles from 'assets/theme/components/cards/charts/card-total-orders.js'

const useStyles = makeStyles(componentStyles)
const useChartStyles = makeStyles(chartComponentStyles)

const themeColors = require('assets/theme/colors.js').default
const { paletteColor, UNCATEGORISED_COLOR } = require('config/constants/ChartPalette')

var colors = {
    gray: themeColors.gray,
    theme: {
        primary: themeColors.primary.main,
        success: themeColors.success.main,
        info: themeColors.info.main,
        warning: themeColors.warning.main,
        danger: themeColors.error.main,
    },
}


function PageAttribution(props) {
    const classes = { ...useStyles(), ...useChartStyles() }
    const theme = useTheme()
    const [open, setOpen] = useState(false)
    if (window.Chart) {
        parseOptions(Chart, chartOptions())
    }

    const leads = props.leads || []

    // Aggregate leads by lastInternalPage
    const pageMap = {}
    let unattributed = { total: 0, allocated: 0, unallocated: 0, revenue: 0, referrers: {}, types: {} }

    for (let lead of leads) {
        const page = lead.lastInternalPage && lead.lastInternalPage.trim() !== '' ? lead.lastInternalPage.trim() : null
        const referrer = lead.referrer && lead.referrer.trim() !== '' ? lead.referrer.trim() : 'Direct / Unknown'
        const isAllocated = !!lead.companyId
        const price = isAllocated && lead.leadPrice ? parseFloat(lead.leadPrice) || 0 : 0
        const type = lead.quote && lead.quote.type && pensionTypes.indexOf(lead.quote.type) > -1 ? lead.quote.type : 'Other'

        if (!page) {
            unattributed.total += 1
            if (isAllocated) {
                unattributed.allocated += 1
                unattributed.revenue += price
            } else {
                unattributed.unallocated += 1
            }
            unattributed.referrers[referrer] = (unattributed.referrers[referrer] || 0) + 1
            if (!unattributed.types[type]) unattributed.types[type] = { count: 0, allocated: 0, revenue: 0 }
            unattributed.types[type].count += 1
            if (isAllocated) {
                unattributed.types[type].allocated += 1
                unattributed.types[type].revenue += price
            }
        } else {
            if (!pageMap[page]) {
                pageMap[page] = { page, total: 0, allocated: 0, unallocated: 0, revenue: 0, referrers: {}, types: {} }
            }
            pageMap[page].total += 1
            if (isAllocated) {
                pageMap[page].allocated += 1
                pageMap[page].revenue += price
            } else {
                pageMap[page].unallocated += 1
            }
            pageMap[page].referrers[referrer] = (pageMap[page].referrers[referrer] || 0) + 1
            if (!pageMap[page].types[type]) pageMap[page].types[type] = { count: 0, allocated: 0, revenue: 0 }
            pageMap[page].types[type].count += 1
            if (isAllocated) {
                pageMap[page].types[type].allocated += 1
                pageMap[page].types[type].revenue += price
            }
        }
    }

    // Build table data sorted by total leads desc
    const tableData = Object.values(pageMap)
        .map(row => ({
            ...row,
            avgRevenue: row.allocated > 0 ? row.revenue / row.allocated : 0,
            topReferrer: Object.entries(row.referrers).sort((a, b) => b[1] - a[1])[0]?.[0] || '-'
        }))
        .sort((a, b) => b.total - a.total)

    // Add unattributed row
    const unattributedRow = {
        page: 'Unattributed',
        total: unattributed.total,
        allocated: unattributed.allocated,
        unallocated: unattributed.unallocated,
        revenue: unattributed.revenue,
        avgRevenue: unattributed.allocated > 0 ? unattributed.revenue / unattributed.allocated : 0,
        topReferrer: Object.entries(unattributed.referrers).sort((a, b) => b[1] - a[1])[0]?.[0] || '-',
        types: unattributed.types
    }

    const allRows = [...tableData, unattributedRow]

    // Summary stats
    const totalLeads = leads.length
    const attributedCount = totalLeads - unattributed.total
    const attributedPct = totalLeads > 0 ? ((attributedCount / totalLeads) * 100).toFixed(1) : 0

    // Stacked bar chart data - top 10 pages by lead count, stacked by pension type
    const chartData = tableData
        .sort((a, b) => b.total - a.total)
        .slice(0, 10)

    const chartLabels = chartData.map(row => {
        const path = row.page.replace('https://nationalpensionhelpline.ie', '').replace('https://www.nationalpensionhelpline.ie', '')
        return path.length > 40 ? path.substring(0, 37) + '...' : path || '/'
    })

    // Determine which types are actually present in chart data, sorted by total volume
    const allTypesInChart = {}
    for (let row of chartData) {
        for (let [type, data] of Object.entries(row.types)) {
            allTypesInChart[type] = (allTypesInChart[type] || 0) + data.count
        }
    }
    const activeTypes = Object.entries(allTypesInChart)
        .sort((a, b) => b[1] - a[1])
        .map(([type]) => type)

    // Build a stable type → color map so chart bars, tooltip and legend swatches all match.
    const typeColorMap = {}
    let _paletteIdx = 0
    for (const type of activeTypes) {
        if (type === 'Other' || type === 'Untyped') {
            typeColorMap[type] = UNCATEGORISED_COLOR
        } else {
            typeColorMap[type] = paletteColor(_paletteIdx)
            _paletteIdx++
        }
    }

    const datasets = activeTypes.map((type) => ({
        label: type,
        data: chartData.map(row => (row.types[type] && row.types[type].count) || 0),
        backgroundColor: typeColorMap[type],
        maxBarThickness: 16,
    }))

    const barData = {
        labels: chartLabels,
        datasets: datasets
    }

    const barOptions = {
        scales: {
            xAxes: [{
                stacked: true,
                gridLines: {
                    color: colors.gray[200],
                    zeroLineColor: colors.gray[200],
                },
                ticks: {
                    beginAtZero: true,
                    precision: 0
                }
            }],
            yAxes: [{
                stacked: true,
                gridLines: {
                    display: false
                },
                ticks: {
                    fontSize: 11
                }
            }]
        },
        tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
                label: function(tooltipItem, data) {
                    const label = data.datasets[tooltipItem.datasetIndex].label
                    const value = tooltipItem.xLabel
                    return value > 0 ? label + ': ' + value : null
                },
                filter: function(tooltipItem, data) {
                    return tooltipItem.xLabel > 0
                }
            }
        },
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                boxWidth: 12,
                fontSize: 11
            }
        }
    }

    // Expandable row renderer - shows pension type breakdown
    const expandRow = {
        renderer: row => {
            const types = row.types || {}
            const sortedTypes = Object.entries(types).sort((a, b) => b[1].count - a[1].count)
            if (sortedTypes.length === 0) {
                return <Box padding='1rem' fontStyle='italic'>No type breakdown available</Box>
            }
            return (
                <Box padding='0.5rem 1rem' style={{ overflow: 'hidden' }}>
                    <Table size='small' style={{ color: 'inherit', border: '1px solid #e9ecef', borderCollapse: 'collapse' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }}><strong>Pension Type</strong></TableCell>
                                <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }} align='right'><strong>Leads</strong></TableCell>
                                <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }} align='right'><strong>Allocated</strong></TableCell>
                                <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }} align='right'><strong>Revenue</strong></TableCell>
                                <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }} align='right'><strong>Avg Revenue</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedTypes.map(([type, data], index) => (
                                <TableRow key={type}>
                                    <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }}>
                                        <Box display='flex' alignItems='center'>
                                            <Box
                                                width='10px'
                                                height='10px'
                                                borderRadius='2px'
                                                marginRight='0.5rem'
                                                style={{ backgroundColor: typeColorMap[type] || UNCATEGORISED_COLOR }}
                                            />
                                            {type}
                                        </Box>
                                    </TableCell>
                                    <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }} align='right'>{data.count}</TableCell>
                                    <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }} align='right'>{data.allocated}</TableCell>
                                    <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }} align='right'>€{data.revenue.toFixed(2)}</TableCell>
                                    <TableCell style={{ color: 'inherit', border: '1px solid #e9ecef' }} align='right'>€{data.allocated > 0 ? (data.revenue / data.allocated).toFixed(2) : '0.00'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )
        },
        showExpandColumn: true,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: () => null,
        expandColumnRenderer: ({ expanded }) => {
            if (expanded) {
                return <Box style={{ cursor: 'pointer', fontWeight: 'bold' }}>▼</Box>
            }
            return <Box style={{ cursor: 'pointer' }}>▶</Box>
        }
    }

    // Table columns
    const columns = [
        {
            dataField: 'page',
            text: 'Last Page',
            sort: true,
            formatter: (cell) => {
                if (cell === 'Unattributed') {
                    return <Box component='span' fontStyle='italic' color='text.secondary'>{cell}</Box>
                }
                const path = cell.replace('https://nationalpensionhelpline.ie', '').replace('https://www.nationalpensionhelpline.ie', '') || '/'
                return <Box component='span' title={cell}>{path}</Box>
            }
        },
        {
            dataField: 'topReferrer',
            text: 'Top Referrer',
            sort: true,
            formatter: (cell) => {
                const display = cell.length > 30 ? cell.substring(0, 27) + '...' : cell
                return <Box component='span' title={cell}>{display}</Box>
            }
        },
        {
            dataField: 'total',
            text: 'Total Leads',
            sort: true,
            sortFunc: (a, b, order) => order === 'asc' ? a - b : b - a
        },
        {
            dataField: 'allocated',
            text: 'Allocated',
            sort: true,
            sortFunc: (a, b, order) => order === 'asc' ? a - b : b - a
        },
        {
            dataField: 'unallocated',
            text: 'Unallocated',
            sort: true,
            sortFunc: (a, b, order) => order === 'asc' ? a - b : b - a
        },
        {
            dataField: 'revenue',
            text: 'Total Revenue',
            sort: true,
            sortFunc: (a, b, order) => order === 'asc' ? a - b : b - a,
            formatter: (cell) => '€' + cell.toFixed(2)
        },
        {
            dataField: 'avgRevenue',
            text: 'Avg Revenue',
            sort: true,
            sortFunc: (a, b, order) => order === 'asc' ? a - b : b - a,
            formatter: (cell) => '€' + cell.toFixed(2)
        }
    ]

    return (
        <>
            <Card style={{ marginBottom: '2rem' }}>
                <CardHeader
                    subheader={
                        <Box display='flex' justifyContent='space-between' alignItems='center'>
                            <Box display='flex' alignItems='center'>
                                <Box
                                    component={Typography}
                                    variant='h2'
                                    marginBottom='0!important'
                                >
                                    <Box component='span'>Page Attribution</Box>
                                </Box>
                                <Box
                                    component='span'
                                    fontSize='.875rem'
                                    marginLeft='1.5rem'
                                    color={theme.palette.gray[600]}
                                >
                                    Attributed: <strong>{attributedCount}/{totalLeads}</strong> ({attributedPct}%)
                                </Box>
                                <Box
                                    component='span'
                                    fontSize='.875rem'
                                    marginLeft='1rem'
                                    color={theme.palette.gray[600]}
                                >
                                    Unattributed: <strong>{unattributed.total}</strong>
                                </Box>
                            </Box>
                            <IconButton size='small' onClick={() => setOpen(o => !o)}>
                                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>
                    }
                    classes={{ root: classes.cardHeaderRoot }}
                />
                <Collapse in={open}>
                    {chartData.length > 0 && (
                        <CardContent>
                            <Box
                                component={Typography}
                                variant='h4'
                                marginBottom='1rem!important'
                            >
                                Top Pages by Lead Volume (by Type)
                            </Box>
                            <Box position='relative' height={Math.max(250, chartData.length * 40) + 'px'}>
                                <HorizontalBar
                                    data={barData}
                                    options={barOptions}
                                />
                            </Box>
                        </CardContent>
                    )}

                    <div className='table-container'>
                        <Box padding='0.5rem 1rem 0' fontSize='.75rem' color={theme.palette.gray[500]}>
                            Click ▶ to expand type breakdown per page
                        </Box>
                        <BootstrapTable
                            keyField='page'
                            data={allRows}
                            columns={columns}
                            filter={filterFactory()}
                            defaultSorted={[{ dataField: 'total', order: 'desc' }]}
                            expandRow={expandRow}
                            noDataIndication={<Box>No lead data available for this period</Box>}
                        />
                    </div>
                </Collapse>
            </Card>
        </>
    )
}

export default PageAttribution
